import React, { useEffect, useRef } from 'react';
import { handleImageErrorWithFallback } from '../../utils/imageManager';
import { renderCtaButton } from '../../utils/ctaHelper';
import { usePageTitle } from '../../hooks/usePageTitle';

const ElegantVariant = ({ heroContent, brand, domainConfig }) => {
  const mainImageRef = useRef(null);
  const secondaryImageRef = useRef(null);
  
  // Get hostname-specific H1 title
  const h1Title = usePageTitle('home');

  // Get domain-specific features and testimonials
  const getDomainFeatures = () => {
    if (domainConfig?.features?.items && domainConfig.features.items.length > 0) {
      return domainConfig.features.items;
    }
    return heroContent.features.map(feature => ({ title: feature, description: '', icon: '✨' }));
  };

  const getDomainTestimonials = () => {
    if (domainConfig?.testimonials && domainConfig.testimonials.length > 0) {
      return domainConfig.testimonials;
    }
    return [
      { 
        content: "The elegant design and sophisticated functionality have elevated our business operations to new heights.",
        author: "Victoria Sterling",
        role: "CEO",
        company: "Prestige Enterprises"
      },
      { 
        content: "A truly refined solution that combines beauty with exceptional performance. Simply outstanding.",
        author: "Alexander Windsor",
        role: "Director",
        company: "Elite Solutions"
      }
    ];
  };

  const domainFeatures = getDomainFeatures();
  const testimonials = getDomainTestimonials();

  // Reset image elements when domain changes
  useEffect(() => {
    if (mainImageRef.current) {
      Object.keys(mainImageRef.current.dataset).forEach(key => {
        if (key.startsWith('triedExtensions_')) {
          delete mainImageRef.current.dataset[key];
        }
      });
    }
    if (secondaryImageRef.current) {
      Object.keys(secondaryImageRef.current.dataset).forEach(key => {
        if (key.startsWith('triedExtensions_')) {
          delete secondaryImageRef.current.dataset[key];
        }
      });
    }
  }, [brand.id]);

  const { mainImage, secondaryImage, title, subtitle, features } = heroContent;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-100">
      {/* Hero Section - Elegant Design */}
      <section className="relative pt-20 pb-32 overflow-hidden">
        {/* Elegant Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-96 h-96 bg-gradient-to-r from-blue-100 to-blue-200 rounded-full opacity-30 blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-80 h-80 bg-gradient-to-r from-blue-100 to-blue-200 rounded-full opacity-25 blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-gradient-to-r from-blue-100 to-blue-200 rounded-full opacity-20 blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6">
          {/* Elegant Brand Badge */}
          <div className="mb-8">
            <span className="inline-block px-6 py-3 text-sm font-medium text-blue-600 bg-white/80 backdrop-blur-sm rounded-full border border-blue-200 shadow-sm">
              {brand.name}
            </span>
          </div>

          {/* Main Content Grid */}
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Column - Content */}
            <div className="space-y-8">
              <div>
                <h1 className="text-5xl lg:text-6xl font-light text-blue-900 leading-tight mb-6 tracking-tight">
                  {h1Title || title}
                </h1>
                <p className="text-xl text-blue-600 leading-relaxed font-light">
                  {subtitle}
                </p>
              </div>

              {/* Features - Elegant List */}
              <div className="space-y-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-4 p-4 bg-white/60 backdrop-blur-sm rounded-xl border border-blue-200/50 shadow-sm hover:shadow-md transition-all duration-300">
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-400 to-blue-500 rounded-full flex items-center justify-center">
                      <div className="w-3 h-3 bg-white rounded-full"></div>
                    </div>
                    <span className="text-blue-700 font-medium">{feature}</span>
                  </div>
                ))}
              </div>

              {/* CTA Buttons - Elegant Style */}
              <div className="flex flex-col sm:flex-row gap-4 pt-8">
                {renderCtaButton(domainConfig?.cta?.primary, 'Get Started', 'px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-medium rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 text-center', domainConfig?.shopLink)}
                {renderCtaButton(domainConfig?.cta?.secondary, 'Learn More', 'px-8 py-4 border-2 border-blue-600 text-blue-800 font-medium rounded-xl hover:border-blue-700 hover:bg-blue-50 transition-all duration-300 text-center', domainConfig?.shopLink)}
              </div>
            </div>

            {/* Right Column - Images */}
            <div className="relative">
              {/* Main Image with Elegant Frame */}
              <div className="relative group">
                <div className="absolute -inset-4 bg-gradient-to-r from-blue-200 to-blue-300 rounded-3xl opacity-50 group-hover:opacity-75 transition-opacity duration-500"></div>
                <div className="relative">
                  <img 
                    ref={mainImageRef}
                    src={mainImage} 
                    alt={title}
                    className="w-full aspect-video object-cover rounded-2xl shadow-2xl"
                    onError={(e) => handleImageErrorWithFallback(e, brand.id, 'hero-main')}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent rounded-2xl"></div>
                </div>
              </div>
              
              {/* Secondary Image - Elegant Overlay */}
              <div className="absolute -bottom-8 -left-8 w-48 h-32 group">
                <div className="absolute -inset-2 bg-gradient-to-r from-blue-200 to-blue-300 rounded-2xl opacity-50 group-hover:opacity-75 transition-opacity duration-500"></div>
                <div className="relative">
                  <img 
                    ref={secondaryImageRef}
                    src={secondaryImage} 
                    alt={`${title} - Secondary`}
                    className="w-full h-full object-cover rounded-xl shadow-xl"
                    onError={(e) => handleImageErrorWithFallback(e, brand.id, 'hero-secondary')}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-xl"></div>
                </div>
              </div>

              {/* Elegant Elements */}
              <div className="absolute top-4 right-4 w-16 h-16 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center border border-blue-200/50 shadow-sm">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-400 to-blue-500 rounded-full flex items-center justify-center">
                  <div className="w-4 h-4 bg-white rounded-full"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section - Elegant Cards */}
      <section className="py-20 bg-white/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { number: '1000+', label: 'Satisfied Customers', color: 'from-amber-500 to-orange-500' },
              { number: '99.9%', label: 'Uptime', color: 'from-rose-500 to-pink-500' },
              { number: '24/7', label: 'Support', color: 'from-indigo-500 to-purple-500' },
              { number: '5★', label: 'Premium Rating', color: 'from-slate-600 to-slate-700' }
            ].map((stat, index) => (
              <div key={index} className="text-center p-6 bg-white/80 backdrop-blur-sm rounded-2xl border border-blue-200/50 shadow-sm hover:shadow-md transition-all duration-300">
                <div className={`text-4xl font-light mb-2 bg-gradient-to-r ${stat.color} bg-clip-text text-transparent`}>
                  {stat.number}
                </div>
                <div className="text-blue-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section - Elegant Showcase */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-light text-blue-900 mb-4 tracking-tight">
              {domainConfig?.features?.title || 'Features'}
            </h2>
            <p className="text-xl text-blue-600 font-light">
              {domainConfig?.features?.subtitle || 'Our solutions are designed to help you optimize your business operations'}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {domainFeatures.map((feature, index) => (
              <div key={index} className="group p-8 bg-white/80 backdrop-blur-sm rounded-2xl border border-blue-200/50 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-2">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-400 to-blue-500 rounded-2xl mx-auto mb-6 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <div className="text-2xl text-white">
                    {feature.icon || (index % 4 === 0 ? '✨' : index % 4 === 1 ? '🎯' : index % 4 === 2 ? '💎' : '🌟')}
                  </div>
                </div>
                <h3 className="text-xl font-medium text-blue-900 mb-4 text-center">{feature.title}</h3>
                <p className="text-blue-600 text-center leading-relaxed font-light">
                  {feature.description || 'Elegantly crafted solutions that combine sophistication with functionality for the discerning professional.'}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section - Elegant Style */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-light text-blue-900 mb-4 tracking-tight">Testimonials</h2>
            <p className="text-xl text-blue-600 font-light">What our clients say about us</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white/80 backdrop-blur-sm rounded-2xl border border-blue-200/50 shadow-sm p-8 hover:shadow-lg transition-all duration-300">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-400 to-blue-500 rounded-full flex items-center justify-center">
                      <span className="text-white font-medium text-lg">
                        {testimonial.author.split(' ').map(n => n[0]).join('')}
                      </span>
                    </div>
                    <div>
                      <p className="text-lg font-medium text-blue-900">
                        {testimonial.author}
                      </p>
                      <p className="text-sm text-blue-500">
                        {testimonial.role}, {testimonial.company}
                      </p>
                    </div>
                  </div>
                  <div className="text-blue-400">
                    <svg className="h-8 w-8" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M14.017 21v-7.391C14.017 10.257 16.274 8 19.625 8 20.937 8 22 9.063 22 10.375v1.25C22 12.937 20.937 14 19.625 14h-1.25v7h-4.358z" />
                      <path d="M3.5 21v-7.391C3.5 10.257 5.757 8 9.108 8 10.42 8 11.483 9.063 11.483 10.375v1.25C11.483 12.937 10.42 14 9.108 14h-1.25v7H3.5z" />
                    </svg>
                  </div>
                </div>
                <p className="text-blue-600 leading-relaxed italic">"{testimonial.content || testimonial.quote}"</p>
                <div className="mt-6 flex">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <svg key={star} className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Elegant Style */}
      <section className="py-20 bg-gradient-to-r from-blue-800 to-blue-900">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-light text-white mb-4 tracking-tight">Ready to Get Started?</h2>
          <p className="text-xl text-blue-100 mb-8 font-light">
            Contact our experts to discuss how our solutions can transform your business operations.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {renderCtaButton(domainConfig?.cta?.primary, 'Request Demo', 'px-8 py-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-medium rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl text-center', domainConfig?.shopLink)}
            {renderCtaButton(domainConfig?.cta?.secondary, 'Buy Now', 'px-8 py-4 border-2 border-white/30 text-white font-medium rounded-xl hover:bg-white/10 transition-all duration-300 text-center', domainConfig?.shopLink)}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ElegantVariant;
